import {
  ButtonDS,
  DialogModalDS,
  InputDS,
  isNameDuplicate,
  Spacer,
} from "@qivia/ui";
import { Folder } from "@qivia/ui/src/assets/assets";
import { t } from "i18next";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { createDepartmentOfCompanyAsync, selectDepartments } from "../departmentsSlice";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { createDepartmentOfCompanyPayload } from "../departmentsApi";
import { SubmitHandler, useForm } from "react-hook-form";
import { selectCompany, selectManager } from "../../homeSlice";

export type FormCreateDepartment = {
  departmentName: string;
};

export const CreateDepartmentModal = (props: {
  visible: boolean;
  onCloseModal: () => void;
}) => {
  const dispatch = useAppDispatch();

  const company = useAppSelector(selectCompany);
  const [department, setDepartment] = useState<string>("");
  const departments = useAppSelector(selectDepartments);
  const manager = useAppSelector(selectManager);

  const { handleSubmit, formState, setValue } = useForm<FormCreateDepartment>();

  const resetValue = useCallback(() => {
    setDepartment("");
  }, []);
  
  const handleCloseModal = () => {
    resetValue();
    props.onCloseModal();
  }
  
  const isDepartmentNameDuplicate: boolean = useMemo(
    () => isNameDuplicate(departments, department.trim()),
    [departments, department],
  );
  
  const isValidForm: boolean = useMemo(
    () => {
      return (
        department.length <= 28 &&
        department !== "" &&
        !isDepartmentNameDuplicate &&
        formState.isValid
      )
    },
    [department, isDepartmentNameDuplicate, formState.isValid],
  );

  const DepartmentError: string | undefined = useMemo(() => {
    if (isDepartmentNameDuplicate) {
      return t("departments.modal.error.nameAlreadyExists");
    }

    return department.length > 28
      ? t("departments.modal.error.length")
      : undefined;
  }, [isDepartmentNameDuplicate, department]);

  const handleDepartmentChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toLocaleUpperCase();
    setDepartment(value);
    setValue("departmentName", value, { shouldValidate: true })
  }, [setValue])
  
  const onSubmitCreateDepartment: SubmitHandler<FormCreateDepartment> = useCallback(
    (data) => {

      if (!company || !manager || !data.departmentName) return;

      if (isValidForm) {
        const createDepartmentData: createDepartmentOfCompanyPayload = {
          companyUuid: company.uuid,
          departmentName: data.departmentName,
          updatedBy: manager.uuid
        };

        void dispatch(createDepartmentOfCompanyAsync(createDepartmentData));

        resetValue();
        props.onCloseModal();
      }
    },
    [company, dispatch, isValidForm, manager, props, resetValue],
  );

  useEffect(() => {
    if (props.visible === false) {
      resetValue();
    }
  }, [props.visible, resetValue]);

  return (
    <DialogModalDS
      visible={props.visible}
      title={t("departments.modal.title") || ""}
      subTitle={t("departments.modal.subTitle") || ""}
      iconTitle={<Folder />}
      onClose={handleCloseModal}
      bottomButtons={{
        leftButton: (
          <ButtonDS
            text={t("departments.modal.cancel")}
            format={"fill"}
            buttonType={"secondary"}
            onClick={handleCloseModal}
          />
        ),
        rightButton: (
          <ButtonDS
            text={t("departments.modal.add")}
            format={"fill"}
            buttonType={"primary"}
            onClick={() => void handleSubmit(onSubmitCreateDepartment)()}
            disabled={!isValidForm}
          />
        ),
      }}
    >
      <StyledModalContent>
        <InputDS
          label={t("departments.modal.department") || ""}
          placeholder={t("departments.modal.department.placeholder") || ""}
          error={DepartmentError}
          value={department}
          onChange={handleDepartmentChange}
          onKeyDown={(e) => e.key === "Enter" && void handleSubmit(onSubmitCreateDepartment)()}
          withoutSpacerBottom
        />
        <Spacer y={0.625} />
      </StyledModalContent>
    </DialogModalDS>
  );
};

const StyledModalContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;
